import { EventsTimeline } from '@tunasong/audio-ui'
import { Spotify as SpotifyIcon } from '@tunasong/icons'
import { isSpotify, logger, shortUuid, type Embed } from '@tunasong/models'
import type { AudioEvent } from '@tunasong/schemas'
import { HBox, Typography, useContainerSize, useDeviceState, VBox } from '@tunasong/ui-lib'
import {
  useEffect,
  useEffectEvent,
  useMemo,
  useRef,
  useState,
  type FC,
  type HTMLAttributes,
  type IframeHTMLAttributes,
} from 'react'
import Services from './services/index.js'
import type { ServiceApi } from './services/types.js'
import styles from './content.module.css'

export interface EmbedContentProps extends HTMLAttributes<HTMLDivElement> {
  element: Embed

  /** Common audio events interface */
  events?: AudioEvent[]
  onCreateEvent?: (event: AudioEvent) => void
  onUpdateEvent?: (event: AudioEvent) => void
  onDeleteEvent?: (event: AudioEvent) => void

  playbackRate?: number
  onPlaybackRateChange?: (playbackRate: number) => void

  /** Callback when the service API becomes available */
  onServiceApi?: (api: ServiceApi) => void

  pollMs?: number
}

export const EmbedContent: FC<EmbedContentProps> = props => {
  const {
    element,
    pollMs = 100,
    onCreateEvent,
    onUpdateEvent,
    onDeleteEvent,
    playbackRate,
    onPlaybackRateChange,
    onServiceApi,
    events = [],
    className: _className,
    ...boxProps
  } = props
  const { url: baseUrl } = element

  const iframeId = useMemo(() => shortUuid(), [])
  // logger.debug('EmbedContent iframe id', iframeId, element)

  const service = Services[element.service]
  const url = service?.getUrlForSize ? service.getUrlForSize(element, element.size) : baseUrl

  const size = Services[element.service]?.sizes[element.size]

  const css: IframeHTMLAttributes<unknown> = size ?? {
    width: '100%',
    height: '300px',
    backgroundColor: 'transparent',
    borderRadius: 0,
  }
  const [el, setEl] = useState<HTMLIFrameElement | null>(null)
  const apiContainerRef = useRef<HTMLDivElement>(null)
  const [serviceApi, setServiceApi] = useState<ServiceApi | null>(null)
  const serviceApiRef = useRef<ServiceApi | null>(null)

  const showEventsTimeline = Boolean(serviceApi?.getCurrentTime && serviceApi?.getDuration && onCreateEvent)

  /** Read the latest element and callback at creation time, without recreating the player when they change */
  const createServiceApi = useEffectEvent((iframe: HTMLIFrameElement, container: HTMLDivElement) => {
    const getApi = Object.entries(Services).find(([name]) => name === element.service)?.[1]?.getApi
    if (!getApi) {
      logger.warn('No service getApi found for', element.service)
      return null
    }
    const api = getApi(element, iframe, container)
    onServiceApi?.(api)
    return api
  })

  /**
   * Create the service player and destroy it in the same effect (GH #2137). React disconnects and reconnects the
   * effects of a moved fiber in dev (dragging an embed down), and `<Activity>` does it in production. With creation
   * and teardown in separate effects, the reconnect kept a destroyed player in state and called it, and the editor
   * crashed. Now a reconnect destroys the player and makes a fresh one.
   */
  const serviceKey = element.service
  useEffect(() => {
    const container = apiContainerRef.current
    if (!(el && container)) {
      return
    }
    const api = createServiceApi(el, container)
    if (!api) {
      return
    }
    serviceApiRef.current = api
    setServiceApi(api)
    return () => {
      api.cleanup?.()
      if (serviceApiRef.current === api) {
        serviceApiRef.current = null
      }
    }
  }, [el, serviceKey])

  /** Notify the service API of the new element */
  useEffect(() => {
    serviceApi?.updateEmbed?.(element)
  }, [element, serviceApi])

  /**
   * Declared after the create effect, and reads the ref it sets: on a reconnect the `serviceApi` state still holds the
   * destroyed player until the next render, and calling it threw (GH #2137). It re-runs when the state catches up.
   */
  useEffect(() => {
    const api = serviceApiRef.current
    if (!(api?.setPlaybackRate && playbackRate)) {
      return
    }
    api.setPlaybackRate(playbackRate)
  }, [playbackRate, serviceApi])

  const handleSeek = (time: number) => {
    if (!serviceApi?.setCurrentTime) {
      logger.warn('No setCurrentTime method available on serviceApi')
      return
    }
    logger.debug('Seeking to time:', time)
    serviceApi.setCurrentTime(time)
  }

  const [duration, setDuration] = useState<number>(0)
  const [position, setPosition] = useState<number>(0)
  /**
   * @todo
   * Factor out in an useInterval hook, or a usePolledState hook
   * or
   * add a onMediaChanged hook or similar to the serviceApi
   * */
  useEffect(() => {
    const interval = setInterval(() => {
      /** The live player, never a destroyed one still in state (GH #2137) */
      const api = serviceApiRef.current
      if (api?.getCurrentTime) {
        setPosition(api.getCurrentTime())
      }
      if (api?.getDuration) {
        setDuration(api.getDuration())
      }
    }, pollMs)

    return () => clearInterval(interval)
  }, [pollMs, serviceApi])

  /** Services with getApi (Spotify, YouTube) render their own player iframe into the
   *  React-managed container. Hide the fallback only once that player exists: showing
   *  both would duplicate, but hiding the fallback while the service script is still
   *  loading — or after it failed (blocked, offline, outage) — leaves an invisible
   *  embed (GH #1727). */
  const [apiPlayerMounted, setApiPlayerMounted] = useState(false)
  useEffect(() => {
    const container = apiContainerRef.current
    if (!container) {
      return
    }
    const update = () => setApiPlayerMounted(container.querySelector('iframe') !== null)
    update()
    const observer = new MutationObserver(update)
    observer.observe(container, { childList: true, subtree: true })
    return () => observer.disconnect()
  }, [])
  const hideIFrame = service?.getApi != null && apiPlayerMounted

  const isSpotifyEmbed = isSpotify(element)
  /** Spotify serves ~30s previews for non-logged-in users. We detect a
   *  logged-in user by observing playback position past 31s — previews
   *  stop at ~30s. Once detected, persist so the banner stays hidden. */
  const [spotifyLoggedIn, setSpotifyLoggedIn] = useDeviceState<boolean>('spotify.loggedIn', false)
  useEffect(() => {
    if (isSpotifyEmbed && position > 31 && !spotifyLoggedIn) {
      setSpotifyLoggedIn(true)
    }
  }, [isSpotifyEmbed, position, spotifyLoggedIn, setSpotifyLoggedIn])
  const showSpotifyLoginPrompt = isSpotifyEmbed && !spotifyLoggedIn
  const [spotifyLoginDismissed, setSpotifyLoginDismissed] = useDeviceState<boolean>('spotify.loginDismissed', false)
  const handleDismissSpotifyLogin = () => {
    setSpotifyLoginDismissed(true)
  }

  // Track the size of the container and update the service API
  const [ref, setRef] = useState<HTMLDivElement | null>(null)
  const rect = useContainerSize(ref)
  useEffect(() => {
    if (!(rect && serviceApiRef.current?.setContainerWidth)) {
      return
    }
    serviceApiRef.current?.setContainerWidth(rect.width)
  }, [rect])

  const hasSpeedControl = serviceApiRef.current?.setPlaybackRate

  return url ? (
    <VBox style={{ flex: 1, width: '100%', ...boxProps.style }} {...boxProps} ref={setRef}>
      {showSpotifyLoginPrompt && !spotifyLoginDismissed ? (
        <HBox
          style={{
            alignItems: 'center',
            gap: 8,
            padding: '4px 8px',
            borderRadius: 4,
            backgroundColor: 'rgba(30, 215, 96, 0.1)',
          }}
        >
          <span style={{ display: 'flex', width: 18, height: 18, color: '#1DB954' }}>
            <SpotifyIcon />
          </span>
          <Typography variant="caption" style={{ flex: 1 }}>
            <a href="https://accounts.spotify.com/login" target="_blank" rel="noopener noreferrer">
              Log in to Spotify
            </a>{' '}
            for full song playback
          </Typography>
          <Typography variant="caption" style={{ cursor: 'pointer', opacity: 0.6 }} onClick={handleDismissSpotifyLogin}>
            dismiss
          </Typography>
        </HBox>
      ) : null}
      {showEventsTimeline ? (
        <EventsTimeline
          position={position}
          loopOnly={true}
          height={16}
          events={events}
          duration={duration}
          onSeek={handleSeek}
          onCreateEvent={onCreateEvent}
          onUpdateEvent={onUpdateEvent}
          onDeleteEvent={onDeleteEvent}
          playbackRate={playbackRate}
          onPlaybackRateChange={hasSpeedControl ? onPlaybackRateChange : undefined}
        />
      ) : null}
      {service?.getApi ? <div ref={apiContainerRef} /> : null}
      <iframe
        {...css}
        className={hideIFrame ? styles.iframeHidden : styles.iframeVisible}
        id={iframeId}
        title={element.type}
        contentEditable={false}
        ref={setEl}
        src={url}
        frameBorder={0}
        referrerPolicy="origin"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; fullscreen; gyroscope; picture-in-picture"
        allowFullScreen
      ></iframe>
    </VBox>
  ) : null
}
